import '@fontsource-variable/inter';
import '@fontsource-variable/source-serif-4';
import './basic.css';
import 'normalize.css';
import './downread-theme.css';
import './App.css';

import AppRouter from './AppRouter';
import DownreadLogo from './components/DownreadLogo';
import { AppContext, AppState } from './context/general';
import { useState, useEffect, useRef } from 'react';
import { Router } from 't-lib-ui/router';

import { getAuthFromStorage, getUserFromStorage, getCurrentUser, persistUser, refreshAuth, getJwt, markLoggedIn, logOut } from './connector/auth';
import { broadcastAuthChange, listenAuthSync } from './connector/auth-sync';
import { FetchError } from 't-lib-ui/connector';
import { ensureGeo, hasStoredGeo } from './connector/geo';
import { loadInitialPreferences, flushPendingPreferences } from './connector/preferences';
import { setFeedNameOverrides } from './connector/feed-name-overrides';
import { setEmbedConsentPrefs } from './components/embed-consent';
import { applyTheme } from './theme';
import { createFeedEngine } from './connector/feed-engine';
import { isAnonMigrationPending, migrateAnonToAccount } from './connector/anon-migration';
import { recordActiveNow } from './connector/lastActive';
import { adminNotificationEngine } from './connector/admin-notification-engine';
import { logWithTime as logEngineState } from './utils/log';
import { getJsonStore } from './connector/json-store';

const initAuth = getAuthFromStorage();
const initUser = getUserFromStorage();
const initPreferences = loadInitialPreferences(initUser);
// With offline reading the user's store lives in IndexedDB and loads asynchronously, so the line
// above saw no pending (unsynced) preference edits. Start loading now; boot re-applies them.
const initStoreReady = initUser && getJsonStore(initUser.id).ready;
setFeedNameOverrides(initPreferences.customFeedNames);
setEmbedConsentPrefs(initPreferences.embedConsent);
applyTheme(initPreferences.theme);

const App: React.FC = () => {
  // True while a just-signed-up anon user's local library is being transferred into the new
  // account (see connector/anon-migration.ts). Initialized from the flag so the overlay shows
  // immediately when the migration boots across a reload. Only when booting logged in: the flag
  // is also present while the account is still unconfirmed (e.g. the confirmation link opened in a
  // new tab), and nothing would ever clear the overlay there — it would block the confirm page.
  // After confirming, setLoggedIn → enableAuthed sets it.
  const [migrating, setMigrating] = useState(() => !!(initAuth && initUser) && isAnonMigrationPending());

  // True while the initial cookie-authenticated refreshAuth is in flight (auth mode only). The JWT
  // now lives only in memory, so on every reload we must fetch a fresh one before any api call —
  // we hold the UI on a loader until it resolves so no request runs without a token.
  // Anonymous boot also waits: the first-ever visit must resolve the visitor's region (GET /auth/geo)
  // before any anon api request, so it hits the correct data-residency instance. Returning anon
  // visitors (geo already cached) skip the loader — no added delay.
  const [booting, setBooting] = useState(() => (initAuth && initUser) ? true : !hasStoredGeo());

  const [state, setState] = useState<AppContext>(() => ({
    state: {
      loginState: (initAuth && initUser) ? 'ok' : (initAuth?.status == 'unconfirmed' ? 'unconfirmed' : 'loggedOut'),
      authData: initAuth,
      currentUser: initUser,
      userPreferences: initPreferences,
    },
    // Anon is the default: createFeedEngine() picks the authenticated engine when logged in
    // (auth+user in storage) and the anonymous engine otherwise. The engine is swapped on
    // login/logout (they're different classes); the effect below enables/tears down each instance.
    feedEngine: createFeedEngine(),

    setLoggedIn: (authData, user) => {
      // Record the durable logged-in hint (survives an iOS cookie eviction, unlike the authToken
      // cookie) so a later boot with a missing cookie can require re-login instead of going anon.
      markLoggedIn();
      // Re-initialize the preferences module for this freshly-logged-in user. A login that does NOT
      // reload the page (the normal case) would otherwise leave the module in its boot-time anonymous
      // mode, so every preference edit is written to anon localStorage and never PUT to the server.
      const userPreferences = loadInitialPreferences(user);
      setFeedNameOverrides(userPreferences.customFeedNames);
      setEmbedConsentPrefs(userPreferences.embedConsent);
      applyTheme(userPreferences.theme);
      // The login flow already persisted auth+user, so the swapped engine resolves to the
      // authenticated one; the engine effect enables it.
      swapEngine({loginState: 'ok', authData, currentUser: user, userPreferences});
      adminNotificationEngine.enable(authData);
      // Tell the other tabs: they navigate to /a and reboot into this session (cookie + storage are
      // shared), so none keeps running a stale — possibly different — account. See auth-sync.ts.
      broadcastAuthChange('login');
    },
    setLoggedOut: () => {
      // logOut() already cleared storage, so the swapped engine resolves to the anon one.
      logEngineState('Logging out, swapping to anonymous FeedEngine');
      // Flip the preferences module back to anonymous mode (no server sync), the mirror of the
      // re-init in setLoggedIn. Otherwise anon preference edits would try to PUT to the server.
      const userPreferences = loadInitialPreferences(undefined);
      setFeedNameOverrides(userPreferences.customFeedNames);
      setEmbedConsentPrefs(userPreferences.embedConsent);
      applyTheme(userPreferences.theme);
      // Clear authData/currentUser from React state too — storage is already wiped by logOut(),
      // but components decide auth vs anon requests by state.authData, and a stale value would
      // keep routing them through authenticatedRequest (failing on /auth/refresh after logout).
      swapEngine({loginState: 'loggedOut', authData: undefined, currentUser: undefined, userPreferences});
      adminNotificationEngine.disable();
      // Tell the other tabs to log out too (they wipe their session and reboot anonymously). Also
      // fires for the anon "delete my data" flow — rebooting other anon tabs onto the now-empty
      // storage is correct there as well.
      broadcastAuthChange('logout');
    },
    setUnconfirmed: (authData) => {
      logEngineState('Setting unconfirmed, disabling FeedEngine');
      setState(s => { s.feedEngine.disable(); return {...s, state: {...s.state, loginState: 'unconfirmed', authData}}; });
      adminNotificationEngine.disable();
      // Auth is now in limbo: the signup/login that got us here replaced the shared authToken cookie
      // with the UNCONFIRMED account's, so every other tab's session is dead — log them out. This tab
      // works on in-memory state throughout the confirm flow, so the receivers' storage wipe doesn't
      // affect it; once confirmation completes, setLoggedIn broadcasts 'login' and all tabs rejoin.
      broadcastAuthChange('logout');
    },
    setReloginRequired: () => {
      logEngineState('Setting relogin required, disabling FeedEngine');
      setState(s => { s.feedEngine.disable(); return {...s, state: {...s.state, loginState: 'reloginRequired', authData: undefined}}; });
      adminNotificationEngine.disable();
    },
    setUserPreferences: (prefs) => {
      // Mirror the new prefs onto the cached `user` blob in localStorage. On reload,
      // App boot reads preferences from getUserFromStorage().preferences and overlays
      // only the still-pending edits — once flush() clears a key from pendingPreferences,
      // the cached user blob is the sole source of truth for that field, so it must be
      // kept up-to-date or the change appears to roll back after a refresh.
      setState((s) => {
        if (!s.state.currentUser)
          return {...s, state: {...s.state, userPreferences: prefs}};
        const updatedUser = {...s.state.currentUser, preferences: prefs};
        persistUser(updatedUser);
        return {...s, state: {...s.state, userPreferences: prefs, currentUser: updatedUser}};
      });
      setFeedNameOverrides(prefs.customFeedNames);
      setEmbedConsentPrefs(prefs.embedConsent);
      applyTheme(prefs.theme);
    },
  }));

  function patchState(stateUpdate: Partial<AppContext["state"]>) {
    setState((s) => ({...s, state: {...s.state, ...stateUpdate}}));
  };

  // Swap the feed engine (login ⇄ logout switch between the authenticated and anonymous engines)
  // and patch the related auth state in one update. createFeedEngine() reads the current storage
  // mode, which the login/logout flow has already set by the time this runs. The outgoing engine is
  // destroyed here (not in the enable effect's cleanup) so a StrictMode mount/unmount/mount cycle
  // doesn't tear down the live engine and drop every component's callback registration.
  function swapEngine(stateUpdate: Partial<AppContext["state"]>) {
    setState((s) => {
      s.feedEngine.destroy();
      return {...s, feedEngine: createFeedEngine(), state: {...s.state, ...stateUpdate}};
    });
  };

  // Mirror the offlineReading preference into the engine (also re-applied to a swapped engine).
  // The authenticated engine then keeps a snapshot of its downloaded library in the user's store.
  // Declared before the enable effect, so an offline start already knows it may use that snapshot.
  const offlineReading = !!state.state.userPreferences.offlineReading;
  useEffect(() => state.feedEngine.setOfflineReading(offlineReading), [state.feedEngine, offlineReading]);

  // Enable the current feed engine for the active session (authenticated when logged in, anonymous
  // otherwise). No teardown on cleanup: the engine is a long-lived singleton for the session and is
  // only replaced via swapEngine (which destroys the old one); enable() is idempotent.
  useEffect(() => {
    const engine = state.feedEngine;
    const auth = getAuthFromStorage();
    const user = getUserFromStorage();
    if (!(auth && user)) {
      // Resolve the region before the anon engine fires its first api request. If it's already cached,
      // enable synchronously (booting was never set for this case). Otherwise wait, then drop the loader.
      if (hasStoredGeo()) {
        engine.enable();
        return;
      }
      ensureGeo().finally(() => {
        setBooting(false);
        engine.enable();
      });
      return;
    }

    const enableAuthed = (a: typeof auth) => {
      const enabled = engine.enable(a);
      adminNotificationEngine.enable(a);
      // Only the anon path resolves geo before enabling; embed-consent defaults need the
      // jurisdiction too, so fetch it in the background on a fresh device (cached forever after).
      if (!hasStoredGeo())
        ensureGeo().catch(e => console.error('Geo lookup failed', e));
      // Stamp "now" as last activity on every app start, so the "Welcome back"/RSS-bankruptcy
      // away-time is measured from real app usage — not only from Home-page visits.
      recordActiveNow();
      // Anon → account sign-up: transfer the local library into the new account, then reload so
      // it loads fresh from GET /api/feeds. On failure, keep the local anon data + flag (the next
      // boot retries) and drop the overlay so the user can still use the empty account.
      if (isAnonMigrationPending()) {
        setMigrating(true);
        Promise.resolve(enabled)
          .then(() => migrateAnonToAccount(a))
          .then(() => window.location.reload())
          .catch(e => { console.error('Anon migration failed', e); setMigrating(false); });
      }
    };

    // A fresh login/switch already set the in-memory JWT — enable directly. On a reload the JWT is
    // gone, so fetch a fresh one from the cookie first. If the cookie is gone/expired (or a forced
    // re-login is required), drop to logged-out. A cookie/JWT mismatch reloads the page (handled in
    // refreshAuth) — don't log out then, the cookie is valid, just for another account.
    if (getJwt()) {
      enableAuthed(auth);
      return;
    }
    // Boot-time preferences missed pending edits stored in IndexedDB (see initStoreReady); re-apply
    // them once loaded. Nothing has mounted a preferences hook yet (we're still on the boot screen).
    const applyStoredPreferences = async () => {
      await initStoreReady;
      const user = getUserFromStorage();
      if (user && getJsonStore(user.id).getBackend() == 'idb')
        state.setUserPreferences(loadInitialPreferences(user));
    };
    (async () => {
      try {
        const refreshed = await refreshAuth(auth);
        // The cached user blob is written at login and drifts from the server afterwards (a plan
        // change from admin tooling, billing, or another session). Re-fetch it while still on the
        // boot loader, so plan-dependent UI (feed-quota cards, the private/newsletter limits) is
        // computed from the real plan from the first paint — never swapped in front of the user.
        try {
          const user = await getCurrentUser(refreshed);
          persistUser(user);
          patchState({ currentUser: user });
        } catch (e) {
          console.warn('User refresh failed; keeping the cached user', e);
        }
        await applyStoredPreferences();
        setBooting(false);
        enableAuthed(refreshed);
      } catch (e: any) {
        await applyStoredPreferences();
        setBooting(false);
        if (e?.message == 'Cookie mismatch, reloading')
          return;
        if (e instanceof FetchError && e.status == 401) {
          // The server REJECTED the cookie (evicted, rotated, or forced re-auth) — only then is a
          // re-login the right answer. Storage is left intact, so a re-login (or a later successful
          // refresh) restores the session seamlessly, and the library never drops to anon mode.
          console.warn('Boot refresh rejected; requiring re-login', e);
          state.setReloginRequired();
          return;
        }
        // Anything else is transient: on an iOS PWA cold start the first fetch often fires before
        // the network is back up (radio wake), and a timeout/5xx looks the same — the cookie may be
        // perfectly valid, so do NOT bounce to the login screen. Enter the app on the cached
        // session (authenticatedRequest recovers the JWT lazily once a request gets through) and
        // retry the refresh in the background; only a definitive 401 then forces the re-login.
        console.warn('Boot refresh failed (non-auth); continuing with cached session', e);
        enableAuthed(auth);
        (async () => {
          for (const delay of [2000, 5000, 15000]) {
            await new Promise(r => setTimeout(r, delay));
            if (getJwt()) return; // some request already recovered the session in the meantime
            try { await refreshAuth(auth); return; }
            catch (e2: any) {
              if (e2 instanceof FetchError && e2.status == 401) { state.setReloginRequired(); return; }
              if (e2?.message == 'Cookie mismatch, reloading') return;
            }
          }
        })();
      }
    })();
  }, [state.feedEngine]);

  // The listener below outlives every render ([] deps), so it reads the CURRENT login state through
  // this ref — the closed-over `state` from the mount render would always show the boot-time value.
  const loginStateRef = useRef(state.state.loginState);
  loginStateRef.current = state.state.loginState;

  // Follow auth changes made in OTHER tabs (see auth-sync.ts). A full page navigation is the robust
  // reaction in both directions: it reboots this tab's engines, preferences and in-memory JWT from
  // the shared cookie + localStorage state the acting tab just established.
  useEffect(() => listenAuthSync((msg) => {
    if (msg.type == 'login') {
      // Another tab logged in (possibly a different account). Do NOT touch localStorage — it holds
      // that tab's fresh session keys. Reboot into it; boot's refreshAuth trusts the cookie.
      window.location.replace('/a');
    } else {
      // Another tab logged out. It already wiped the shared storage, but wipe locally too (idempotent,
      // preserves anon-* keys): message delivery and cross-tab storage visibility aren't ordered, and
      // this also drops this tab's in-memory JWT deterministically. Then reboot: a tab that held a
      // session lands on /logged-out (explains the forced logout, offers re-login vs anon mode); a
      // tab that was already anonymous (the anon "delete my data" broadcast) reboots onto the start
      // page as before — it never had a session to lose.
      const hadSession = loginStateRef.current != 'loggedOut';
      logOut();
      window.location.replace(hadSession ? '/logged-out' : '/');
    }
  }), []);

  // Listen for feed changes and update currentUser
  useEffect(() => {
    const unsubscribe = state.feedEngine.onFeedsChange((feeds) => {
      setState((s) => {
        if (!s.state.currentUser) return s;
        const updatedUser = {
          ...s.state.currentUser,
          feeds: feeds.map(f => f.id)
        };
        // Persist updated user to localStorage
        persistUser(updatedUser);
        return {...s, state: {...s.state, currentUser: updatedUser}};
      });
    });

    return unsubscribe;
  }, [state.feedEngine]);

  // Handle visibility changes for pause/resume
  useEffect(() => {
    const engine = state.feedEngine;
    const handleVisibilityChange = () => {
      if (document.hidden) {
        logEngineState('Document hidden, pausing FeedEngine');
        engine.pause();
        // Flush any debounced preference edits now: a mobile PWA may be killed
        // while backgrounded before the 30s save timer fires, losing the change.
        flushPendingPreferences().catch(e => console.error('Error flushing preferences on hide', e));
      } else {
        logEngineState('Document visible, resuming FeedEngine');
        engine.resume();
        recordActiveNow();
      }
    };

    document.addEventListener('visibilitychange', handleVisibilityChange);

    return () => {
      document.removeEventListener('visibilitychange', handleVisibilityChange);
    };
  }, [state.feedEngine]);

  if (booting)
    return (
      <div className="bootScreen">
        <div className="bootCard">
          <div className="bootLogo">
            <DownreadLogo size={72} />
          </div>
          <div className="bootBar" />
        </div>
      </div>
    );

  return (
    <AppState.Provider value={state}>
      <Router>
        <AppRouter />
      </Router>
      {migrating && (
        <div className="anonMigrationOverlay">
          <div className="anonMigrationCard">
            <div className="anonMigrationSpinner" />
            <div className="anonMigrationText">Importing your feeds…</div>
          </div>
        </div>
      )}
    </AppState.Provider>
  );
};

export default App;
